import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Container, Typography, Button, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import releasesData from '../data/releases.json';
import PurchaseLockAnimation from './PurchaseLockAnimation';
import { PURCHASE_STATES } from '../constants/purchaseStates';
import { storeVerifiedEntitlement } from '../utils/purchaseEntitlement';
import { useAudioPlayer } from './AudioPlayer';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';

const Success = () => {
    const { openPlayer, hidePlayer } = useAudioPlayer();
    const reducedMotion = usePrefersReducedMotion();
    const verifiedSession = useRef('');
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
    const successLottieSize = isXs ? 220 : isMdUp ? 420 : 320;
    const [purchaseState, setPurchaseState] = useState(PURCHASE_STATES.VERIFYING);
    const [audioUrlMP3, setAudioUrlMP3] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [releaseTitle, setReleaseTitle] = useState('');
    const location = useLocation();
    const sessionId = new URLSearchParams(location.search).get('session_id');
    const [showUnlockedContent, setShowUnlockedContent] = useState(false);

    useEffect(() => {
        const unreleased = releasesData.find(release => release.platform === 'Unreleased');
        if (unreleased) {
            setReleaseTitle(unreleased.title);
        }
    }, []);

    useEffect(() => {
        hidePlayer();
        verifiedSession.current = '';
        setShowUnlockedContent(false);
        setPurchaseState(PURCHASE_STATES.VERIFYING);
        setAudioUrlMP3('');
        if (!sessionId) {
            setPurchaseState(PURCHASE_STATES.ERROR);
            setErrorMessage('Missing checkout session. Please return home and try again.');
            return;
        }

        let isCancelled = false;

        axios.post('/api/verify-payment', { sessionId })
            .then(response => {
                if (isCancelled) return;

                if (response.data?.verified && response.data?.urlMP3) {
                    verifiedSession.current = sessionId;
                    setAudioUrlMP3(response.data.urlMP3);
                    storeVerifiedEntitlement({ audioUrl: response.data.urlMP3, sessionId });
                    setPurchaseState(PURCHASE_STATES.UNLOCKED);
                    return;
                }

                setPurchaseState(PURCHASE_STATES.ERROR);
                setErrorMessage('Your payment is not verified yet. Please try again.');
            })
            .catch(() => {
                if (isCancelled) return;
                setPurchaseState(PURCHASE_STATES.ERROR);
                setErrorMessage('Verification failed. Please try again.');
            });

        return () => {
            isCancelled = true;
        };
    }, [sessionId, hidePlayer]);

    useEffect(() => {
        if (purchaseState === PURCHASE_STATES.UNLOCKED && reducedMotion) setShowUnlockedContent(true);
    }, [purchaseState, reducedMotion]);

    useEffect(() => {
        if (showUnlockedContent && audioUrlMP3 && releaseTitle && verifiedSession.current === sessionId) {
            openPlayer({ title: releaseTitle, url: audioUrlMP3, sessionId });
        }
    }, [showUnlockedContent, audioUrlMP3, releaseTitle, sessionId, openPlayer]);

    return (
        <Container>
            {purchaseState === PURCHASE_STATES.UNLOCKED ? (
                <Box textAlign="center" mt={5}>
                    {!showUnlockedContent && (
                        <>
                            <PurchaseLockAnimation
                                state={PURCHASE_STATES.UNLOCKED}
                                lottieSize={successLottieSize}
                                onAnimationComplete={() => setShowUnlockedContent(true)}
                            />
                            <Box mt={28} marginBottom={50}>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/"
                                >
                                    Back to Home
                                </Button>
                            </Box>
                        </>
                    )}
                    {showUnlockedContent && (
                        <>
                            <Typography variant="h4" gutterBottom>
                                Thank you for your support!
                            </Typography>
                            <Box mt={8}>
                                <Typography variant="body1" gutterBottom>
                                    You can now listen to the latest release:
                                </Typography>
                            </Box>
                            <Box mt={5}>
                                <Typography variant="h5" gutterBottom>
                                    {releaseTitle}
                                </Typography>
                                <Button variant="contained" onClick={() => openPlayer({
                                    title: releaseTitle, url: audioUrlMP3, sessionId,
                                })}>Open player</Button>
                            </Box>
                            <Box mt={25} marginBottom={50}>
                                <Button
                                    variant="outlined"
                                    component={Link}
                                    to="/"
                                >
                                    Back to Home
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            ) : purchaseState === PURCHASE_STATES.VERIFYING ? (
                <Box textAlign="center" mt={5}>
                    <PurchaseLockAnimation lottieSize={successLottieSize} state={PURCHASE_STATES.VERIFYING} />
                    <Typography variant="h4" gutterBottom>
                        Verifying payment...
                    </Typography>
                    <Box mt={2}>
                        <Typography variant="body2">
                            We are unlocking your song now.
                        </Typography>
                    </Box>
                    <Box mt={18} marginBottom={50}>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/"
                        >
                            Back to Home
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Box textAlign="center" mt={10}>
                    <PurchaseLockAnimation state={PURCHASE_STATES.ERROR} />
                    <Typography variant="h5" gutterBottom>
                        We hit a snag while unlocking your song.
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                        {errorMessage}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        Try again from Home or reach out through the Contact page if needed.
                    </Typography>
                    <Box mt={6} marginBottom={50}>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/"
                        >
                            Back to Home
                        </Button>
                    </Box>
                </Box>
            )}
        </Container>
    );
};

export default Success;
